import React from 'react';
import Setting from './../Common/Setting';
import {IGlobalStorefrontSettings, IStorefrontSettings} from '../../models/Storefront';
import {
    DefaultButton,
    IconButton,
    IContextualMenuProps,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    Pivot,
    PivotItem,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    TextField
} from '@fluentui/react';
import {NEW_STOREFRONT} from './NewStorefrontPanel';
import StorefrontPreview from './StorefrontPreview';
import {
    useGetStorefrontQuery,
    useResetStorefrontCacheSeedMutation,
    useSaveStorefrontMutation } from '../../store/endpoints/StorefrontSettingsEndpoints';
import StorefrontBasicSettingsForm from "./StorefrontBasicSettingsForm";
import StorefrontPaymentShippingSettingsForm from "./StorefrontPaymentShippingSettingsForm";
import StorefrontStyleSettingsForm from "./StorefrontStyleSettingsForm";
import StorefrontSectionEditor from "./StorefrontSectionsEditor";
import StorefrontBannerEditor from "./StorefrontBannerEditor";
import StorefrontLinksEditor from "./StorefrontLinksEditor";
import ToggleField from "../Common/ToggleField";
import NumberField from "../Inputs/NumberField";
import OrderEmailTemplateField from './OrderEmailTemplateField';
import RichTextField from "../Common/RichTextField";
import {
    PREPARED_CART_EMAIL_INITIAL_TEMPLATE,
    PREPARED_CART_QUESTION_EMAIL_TEMPLATE,
    QUOTE_REQUEST_EMAIL_INITIAL_TEMPLATE,
    QUOTE_REQUEST_SALES_REP_EMAIL_TEMPLATE,
    SHARE_CART_EMAIL_TEMPLATE
} from "../../Constants";
import StorefrontOpportunityExtensionForm from "./StorefrontOpportunityExtensionForm";
import StorefrontQuestionsForm from "./StorefrontQuestionsForm";
import StorefrontAccessSettingsForm from "./StorefrontAccessSettingsForm";
import StorefrontTermsAndConditionsForm from "./StorefrontTermsAndConditionsForm";
import StorefrontProductSettingsForm from "./StorefrontProductSettingsForm";
import Accordion from "../Common/Accordion";


interface IStorefrontDetailsPanelProps {
    storefrontId: string;
    globalSettings?: IGlobalStorefrontSettings;
    isOpen: boolean;
    onClose: () => void;
}

const StorefrontDetailsPanel: React.FC<IStorefrontDetailsPanelProps> = (props) => {
    
    const getStorefront = useGetStorefrontQuery(props.storefrontId ?? '', {skip: !props.storefrontId || props.storefrontId === ''});
    const [storefront, setStorefront] = React.useState<IStorefrontSettings>();
    const [saving, setSaving] = React.useState<boolean>(false);
    const [updateStorefront] = useSaveStorefrontMutation();
    const [resetCacheSeed] = useResetStorefrontCacheSeedMutation();
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
    const [externalDataLoading, setExternalDataLoading] = React.useState<boolean>(false);
    const [saveSuccess, setSaveSuccess] = React.useState<boolean>(false);
    const [resettingCacheSeed, setResettingCacheSeed] = React.useState<boolean>(false);
    
    React.useEffect(() =>{
        if (getStorefront.data) {
            setStorefront(()=> getStorefront.data);
        }
        else if(!props.storefrontId || props.storefrontId === '') {
            setStorefront(NEW_STOREFRONT);
        }
        
    }, [getStorefront.data, props.storefrontId, props.isOpen])


    const save = (close?: boolean) => {
        if (props.storefrontId == null) return;
        setSaving(true);
        setErrorMessage(undefined)
        if(storefront){
        updateStorefront({storefront, id: props.storefrontId}).unwrap().then((e) => {
            setSaving(false);
            if(e.success) {
                if(close) {
                    props.onClose();
                }
                else {
                    setSaveSuccess(true);
                    setTimeout(() => setSaveSuccess(false), 3000);
                }
            }
            else{
                setErrorMessage(e.message ?? "an error occurred saving storefront");
            }
        }).catch(() => {
            setSaving(false);
            setErrorMessage("an error occurred saving storefront");
        });
        }
    }

    const close = () => {
        setStorefront(undefined);
        setErrorMessage(undefined)
        props.onClose()
    }
    const menuProps: IContextualMenuProps = {
        items: [
            {
                key: 'saveAndClose',
                text: 'Save and Close',
                iconProps: { iconName: 'SaveAndClose' },
                onClick: () => {
                    save(true);
                }
            }
        ],
    };
    
    const footer = () => (
        <div style={{background: "white"}}>
            {saveSuccess && 
                <div  style={{paddingBottom: '1em'}}>
                    <MessageBar messageBarType={MessageBarType.success}>Successfully saved</MessageBar>
                </div>}
            <Stack horizontal tokens={{childrenGap: '1em'}}>
                <PrimaryButton split menuProps={menuProps} text='Save' onClick={() => save()} disabled={saving || externalDataLoading} />
                <DefaultButton text='Cancel' onClick={close} disabled={saving} />
            </Stack>
        </div>
    )

    const header = () => (
        <div style={{flexGrow: 1, paddingLeft: '25px', background: "white"}}>
            <Stack horizontal verticalAlign="center" tokens={{childrenGap: '0.5em'}}>
                <h1 style={{margin: 0}}>{storefront?.name}</h1>
                {getStorefront.data?.subUrl &&
                    <div>
                        {resettingCacheSeed ? <Spinner style={{paddingLeft: '6px'}} size={SpinnerSize.small} /> : 
                        <IconButton
                            iconProps={{iconName: 'Refresh'}}
                            title="Reset Cache Seed"
                            ariaLabel="Reset Cache Seed"
                            onClick={async () => {
                                setResettingCacheSeed(true);
                                try {
                                    await resetCacheSeed({subUrl: getStorefront.data?.subUrl ?? ""}).unwrap();
                                } catch (error) {
                                    setErrorMessage('Failed to reset cache seed.');
                                } finally {
                                    setResettingCacheSeed(false);
                                }
                            }}
                        />}
                    </div>
                }
            </Stack>
            {errorMessage &&
                <MessageBar messageBarType={MessageBarType.error}>{errorMessage}</MessageBar>
            }
        </div>
    )
    
    return (
        <Panel
            onOuterClick={() => {console.log("swallowing modal close event")}}
            type={PanelType.large}
            headerText={storefront?.name}
            isOpen={props.isOpen}
            onDismiss={close}
            closeButtonAriaLabel='Close'
            isFooterAtBottom={true}
            onRenderFooterContent={footer}
            onRenderHeader={header}
            styles={{
                commands: {
                    background: "white"
                },
                footer: {
                    background: "white"
                },
                navigation: {
                    flex: '1 1 auto',
                },
            }}
        >
            {storefront &&
            <Pivot overflowBehavior='menu' styles={{itemContainer: {marginTop: '1em'}}}>
                <PivotItem headerText='Basic Settings'>
                    <Stack horizontal tokens={{childrenGap: '1em'}}>
                        <div style={{width: '90%', minWidth: '300px'}}>
                            <StorefrontBasicSettingsForm
                                globalSettings={props.globalSettings}
                                settings={storefront}
                                onChange={(updated) => setStorefront(() => ({...updated}))}
                                onLoading={(value) => {
                                setExternalDataLoading(value)}}
                            />
                        </div>
                    </Stack>
                </PivotItem>
                <PivotItem headerText='Product Settings'>
                    <Stack horizontal tokens={{childrenGap: '1em'}}>
                        <div style={{width: '90%', minWidth: '300px'}}>
                            <StorefrontProductSettingsForm
                                globalSettings={props.globalSettings}
                                settings={storefront}
                                onChange={(updated) => setStorefront(() => ({...updated}))}
                                onLoading={(value) => {
                                    setExternalDataLoading(value)}}
                            />
                        </div>
                    </Stack>
                </PivotItem>
                <PivotItem headerText='Access Settings'>
                    <StorefrontAccessSettingsForm
                        globalSettings={props.globalSettings}
                        settings={storefront}
                        onChange={(updated) => setStorefront(() => ({...updated}))}
                        onLoading={(value) => {
                            setExternalDataLoading(value)}}
                    />
                </PivotItem>
                <PivotItem headerText='Terms and Conditions'>
                    <StorefrontTermsAndConditionsForm
                        globalSettings={props.globalSettings}
                        settings={storefront}
                        onChange={(updated) => setStorefront(() => ({...updated}))}
                        onLoading={(value) => {
                            setExternalDataLoading(value)}}
                    />
                </PivotItem>
                <PivotItem headerText='Look and Feel'>
                    <Stack tokens={{childrenGap: '1em'}} horizontal>
                        <StorefrontStyleSettingsForm
                            globalSettings={props.globalSettings}
                            settings={storefront}
                            onChange={(updated) => setStorefront(() => ({...updated}))}
                            onLoading={(value) => {
                                setExternalDataLoading(value)}
                        }
                        />
                        <div style={{paddingTop: '1em', display: 'flex', flexDirection: 'column', gap: '1em'}}>
                            <StorefrontPreview globalSettings={props.globalSettings} storefrontSettings={storefront} />
                        </div>
                    </Stack>
                </PivotItem>
                <PivotItem headerText='Sections'>
                    <ToggleField settingId='storefront.sections.ignoreGlobalSections' inline={true} label={'Ignore Global Sections'} checked={storefront.ignoreGlobalSections} onChange={(e, v) => setStorefront({...storefront, ignoreGlobalSections: v ?? false})} />
                    <Stack tokens={{childrenGap: '1em'}} horizontal>
                        <StorefrontSectionEditor
                            settings={storefront}
                            globalSettings={props.globalSettings}
                            storefront={storefront.id}
                        />
                    </Stack>
                </PivotItem>
                <PivotItem headerText='Links'>
                    <ToggleField settingId='storefront.links.ignoreGlobalLinks' inline={true} label={'Ignore Global Links'} checked={storefront.ignoreGlobalLinks} onChange={(e, v) => setStorefront({...storefront, ignoreGlobalLinks: v ?? false})} />

                    <Stack tokens={{childrenGap: '1em'}} horizontal>
                        <StorefrontLinksEditor
                            storefront={storefront.id}
                        />
                    </Stack>
                </PivotItem>
                <PivotItem headerText={'Banners'}>
                    <ToggleField settingId='storefront.banners.ignoreGlobalBanners' inline={true} label={'Ignore Global Banners'} checked={storefront.ignoreGlobalBanners} onChange={(e, v) => setStorefront({...storefront, ignoreGlobalBanners: v ?? false})} />
                    <div style={{width: '200px'}}>
                        <NumberField settingId='storefront.banners.height' toolTip={'Define a height for the banner container. Recommended for when your banners vary in dimensions to prevent screen jumping.'} label={'Banner Height'} value={storefront.bannerHeight} onChange={(v) => setStorefront({...storefront, bannerHeight: v})} suffix={'px'}/>
                    </div>
                    <StorefrontBannerEditor
                        storefront={storefront.id}
                    />
                </PivotItem>
                <PivotItem headerText='Email'>
                    <Stack tokens={{childrenGap: '1em'}}>
                        <Setting id='storefront.ordering.orderEmail'>
                            <TextField type='email' label='Order Email BCC' value={storefront.orderEmail} onChange={(e, v) => setStorefront({...storefront, orderEmail: v})}
                                       placeholder={props.globalSettings?.orderEmail}/>
                        </Setting>
                        <Setting id='storefront.ordering.customerEmailSubject'>
                            <TextField label='Customer Email Subject' value={storefront.customerEmailSubject}
                                       onChange={(e, v) => setStorefront({...storefront, customerEmailSubject: v})}
                                       placeholder={props.globalSettings?.customerEmailSubject}/>
                        </Setting>
                        {/* One field where there were three, matching the Ordering pages and the
                            global form. The toggle only chose between this editor and a text box the
                            stock template already renders through {{{customer_email_text}}}.

                            Customer Email Text is deprecated, not deleted: the setting and its token
                            are untouched, so a storefront with text there and no template of its own
                            keeps sending exactly what it sends today. The editor opens on the stock
                            template with that text already substituted in, so saving carries it
                            across rather than dropping it. */}
                        <OrderEmailTemplateField
                            value={storefront.customEmailTemplate}
                            customerEmailText={storefront.customerEmailText ?? props.globalSettings?.customerEmailText}
                            onChange={(value) => setStorefront({
                                ...storefront,
                                customEmailTemplate: value,
                                // Derived, as on the other screens: the send path still reads this.
                                useCustomEmailTemplate: !!value.trim(),
                            })}
                        />
                        <Accordion indentWidth={20} title='Prepared Cart Email' beginExpanded={false} useChevron={true}>
                            <RichTextField settingId='storefront.ordering.preparedCartEmailBody' rawOnly={true} mode={'html'} label={'Prepared Cart Email Template'} value={storefront.preparedCartEmailBody ?? PREPARED_CART_EMAIL_INITIAL_TEMPLATE} onChange={(value) => setStorefront({...storefront, preparedCartEmailBody: value}) }/>
                            <Setting id='storefront.ordering.preparedCartEmailSubject'>
                                <TextField label='Prepared Cart Email Subject' value={storefront.preparedCartEmailSubject}
                                           onChange={(e, v) => setStorefront({...storefront, preparedCartEmailSubject: v})}/>
                            </Setting>
                            <RichTextField settingId='storefront.ordering.preparedCartCustomerCommentBody' rawOnly={true} mode={'html'} label={'Customer Comment Email Template'} value={storefront.preparedCartCustomerCommentBody ?? PREPARED_CART_QUESTION_EMAIL_TEMPLATE} onChange={(value) => setStorefront({...storefront, preparedCartCustomerCommentBody: value}) }/>
                            <Setting id='storefront.ordering.preparedCartCustomerCommentSubject'>
                                <TextField label='Customer Comment Subject' value={storefront.preparedCartCustomerCommentSubject}
                                           onChange={(e, v) => setStorefront({...storefront, preparedCartCustomerCommentSubject: v})}/>
                            </Setting>
                        </Accordion>
                        <Accordion indentWidth={20} title='Quote Request Email' beginExpanded={false} useChevron={true}>
                            <RichTextField settingId='storefront.ordering.customerQuoteRequestEmailBody' rawOnly={true} mode={'html'} label={'Customer Quote Request Email Template'} value={storefront.customerQuoteRequestEmailBody ?? QUOTE_REQUEST_EMAIL_INITIAL_TEMPLATE} onChange={(value) => setStorefront({...storefront, customerQuoteRequestEmailBody: value}) }/>
                            <Setting id='storefront.ordering.customerQuoteRequestEmailSubject'>
                                <TextField label='Customer Quote Request Email  Subject' value={storefront.customerQuoteRequestEmailSubject}
                                           onChange={(e, v) => setStorefront({...storefront, customerQuoteRequestEmailSubject: v})}/>
                            </Setting>
                            <RichTextField settingId='storefront.ordering.salesRepQuoteRequestEmailBody' rawOnly={true} mode={'html'} label={'Quote Request Sales Rep Email Template'} value={storefront.salesRepQuoteRequestEmailBody ?? QUOTE_REQUEST_SALES_REP_EMAIL_TEMPLATE} onChange={(value) => setStorefront({...storefront, salesRepQuoteRequestEmailBody: value}) }/>
                            <Setting id='storefront.ordering.salesRepQuoteRequestEmailSubject'>
                                <TextField label='Quote Request Sales Rep Email Subject' value={storefront.salesRepQuoteRequestEmailSubject}
                                           onChange={(e, v) => setStorefront({...storefront, salesRepQuoteRequestEmailSubject: v})}/>
                            </Setting>
                            <Setting id='storefront.ordering.quoteRequestBcc'>
                                <TextField label='Quote Request BCC' value={storefront.quoteRequestBcc}
                                           placeholder='sales@example.com, manager@example.com'
                                           onChange={(e, v) => setStorefront({...storefront, quoteRequestBcc: v})}/>
                            </Setting>
                        </Accordion>
                        <Accordion indentWidth={20} title='Shared Cart Email' beginExpanded={false} useChevron={true}>
                            <RichTextField settingId='storefront.ordering.sharedCartEmailBody' rawOnly={true} mode={'html'} label={'Shared Cart Email Template'} value={storefront.sharedCartEmailBody ?? SHARE_CART_EMAIL_TEMPLATE} onChange={(value) => setStorefront({...storefront, sharedCartEmailBody: value}) }/>
                            <Setting id='storefront.ordering.sharedCartEmailSubject'>
                                <TextField label='Shared Cart Email Subject' value={storefront.sharedCartEmailSubject}
                                           onChange={(e, v) => setStorefront({...storefront, sharedCartEmailSubject: v})}/>
                            </Setting>
                        </Accordion>
                    </Stack>
                </PivotItem>
                <PivotItem headerText='Opportunity Settings'>
                    <StorefrontOpportunityExtensionForm
                        showQuoteRequestSettings={true}
                        subExtensionFolioCompositeId={storefront.opportunityExtension}
                        globalSalesOrderEnabled={props.globalSettings?.enableSalesOrderCreation}
                        settings={storefront}
                        onChange={(settings) => setStorefront({...settings})}
                        onLoading={(isLoading) => setExternalDataLoading(isLoading)}
                    />
                    
                </PivotItem>
                <PivotItem headerText='Order Settings'>
                    <Stack horizontal tokens={{childrenGap: '1em'}}>
                        <div style={{width: '90%', minWidth: '300px'}}>
                            <StorefrontPaymentShippingSettingsForm
                                globalSettings={props.globalSettings}
                                settings={storefront}
                                onChange={(updated) => setStorefront(() => ({...updated}))}
                                onLoading={(value) => {
                                    setExternalDataLoading(value)}}
                            />
                        </div>
                    </Stack>
                </PivotItem>
                <PivotItem headerText='Order Questions'>
                    <Stack horizontal tokens={{childrenGap: '1em'}}>
                        <div style={{width: '90%', minWidth: '300px'}}>
                            <StorefrontQuestionsForm 
                                settings={storefront} 
                                globalSettings={props.globalSettings} 
                                onChange={(v) => {
                                    setStorefront({...v})
                                }}
                            />
                        </div>
                    </Stack>
                </PivotItem>
            </Pivot>
            }
        </Panel>
    );
};

export default StorefrontDetailsPanel;